import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ClockFill } from '@lyearn/icons';
import { useSnackbar } from '@lyearn/ui';
import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import { IN_PROGRESS_JOBS_STATUS } from '@/modules/corporate/modules/bulk-import/hooks/useImportProps';
import { UserCollectionFragment } from '@/modules/corporate/modules/group/views/GroupManager/graphql/fragments/generated/UserCollection';
import useGroups from '@/modules/corporate/modules/group/views/GroupManager/hooks/useGroups';
import {
  getSortAndFilterItems,
  GROUP_SORT_DEFAULT_VALUE,
} from '@/modules/corporate/modules/group/views/GroupManager/utils';
import { GroupStatusEnum, GroupTypeEnum, JobEntityStatusEnum } from '@/types/schema';

import { useIsGroupVisible } from '../../SeeAllLearnerAndGroup/hooks/useSeeAllLearnerAndGroups';
import { Tabs } from '../types';
import { getGroupSelectionConfig, getTabs, OVERRIDES } from '../utils';
import useAsyncListSelectAllManager from './useAsyncListSelectAllManager';
import useLearner from './useLearner';
import useSelectedGroups from './useSelectedGroups';

const EMPTY_ARRAY: never[] = [];
const EMPTY_FILTERS = {};

export default function useLearnerAndGroupDialog({
  onAddLearnerAndGroup,
  selectedLearnerIds,
  selectedUserGroupIds,
  disabled,
  hideGroupsTab,
  hideUsersTab = false,
  isInGroupCheckId,
  importedUserGroupIds,
  removedUserIds,
}: {
  onAddLearnerAndGroup: (
    learnerIds: string[],
    userGroupIds: string[],
    importedUserGroupIds?: string[],
    removedUserIds?: string[],
  ) => void;
  selectedLearnerIds: string[];
  selectedUserGroupIds: string[];
  disabled?: boolean;
  hideGroupsTab?: boolean;
  hideUsersTab?: boolean;
  isInGroupCheckId?: string;
  importedUserGroupIds?: string[];
  removedUserIds?: string[];
}) {
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inViewTab, setInViewTab] = useState(hideUsersTab ? Tabs.Groups : Tabs.Learners);
  const { fetchSelectedGroupIds } = useSelectedGroups();
  const [importJobStatus, setImportJobStatus] = useState<JobEntityStatusEnum | undefined>();
  const isImportInProgress = Boolean(
    importJobStatus && IN_PROGRESS_JOBS_STATUS.includes(importJobStatus),
  );

  const isGroupVisible = useIsGroupVisible() && !Boolean(hideGroupsTab);
  const isUserVisible = !Boolean(hideUsersTab);

  const tabs = useMemo(() => {
    return {
      value: inViewTab,
      tabs: getTabs({ t, isGroupVisible, isUserVisible }),
      onChange: (val: string) => setInViewTab(val as Tabs),
      overrides: OVERRIDES,
    };
  }, [inViewTab, isGroupVisible, t, isUserVisible]);
  const learnersFilterManager = useFilterManagerWithState({
    defaultSortBy: EMPTY_ARRAY,
    defaultFilters: EMPTY_FILTERS,
  });
  const groupsFilterManager = useFilterManagerWithState({
    defaultSortBy: EMPTY_ARRAY,
    defaultFilters: EMPTY_FILTERS,
  });

  const learnersData = useLearner({
    pause: inViewTab !== Tabs.Learners,
    selectedLearnerIds,
    filterManager: learnersFilterManager,
    isInGroupCheckId,
    removedUserIds,
    userGroupIds: importedUserGroupIds,
  });
  const groupSortAndFilterItems = useMemo(() => getSortAndFilterItems(t), [t]);

  const groupData = useAsyncListSelectAllManager<UserCollectionFragment>({
    selectedListItemIds: selectedUserGroupIds,
    filterManager: groupsFilterManager,
    disabled,
    useData: () => {
      const { filter, search, sort } = groupsFilterManager.state;
      const { groups, isFetching, fetchMore, totalCount, error } = useGroups({
        filters: {
          ...filter,
          name: search,
          systemGenerated: false,
          status: [GroupStatusEnum.Published],
          types: [GroupTypeEnum.UserCollection],
        },
        pause: inViewTab !== Tabs.Groups,
        sort: sort?.length ? sort : GROUP_SORT_DEFAULT_VALUE,
      });

      return {
        listItems: groups as UserCollectionFragment[],
        isFetching,
        fetchMore,
        totalCount,
        error,
      };
    },
  });

  const dialogData = {
    [Tabs.Learners]: {
      ...learnersData,
      filterManager: learnersFilterManager,
      error: learnersData.error,
    },
    [Tabs.Groups]: {
      ...groupData,
      sortAndFilterItems: groupSortAndFilterItems,
      filterManager: groupsFilterManager,
      error: groupData.error,
    },
  };

  const onSubmit = useCallback(async () => {
    if (isImportInProgress) {
      showSnackbar({
        message: t('Admin:LearnerAndGroups.ImportLearnersInProgress'),
        iconStatus: 'info',
        StartIcon: ClockFill,
      });
      return;
    }
    setIsSubmitting(true);

    const groupsSelectionConfig = getGroupSelectionConfig(groupData, groupsFilterManager);
    const groupIds = groupsSelectionConfig
      ? await fetchSelectedGroupIds({
          groupsSelectionConfig,
          selectedGroupIds: selectedUserGroupIds,
        })
      : selectedUserGroupIds;
    setIsSubmitting(false);

    const learnerIds = learnersData.asyncList.state.includeIds;
    const removedUserIds = learnersData.asyncList.state.excludeIds;
    const importedUserGroupIds = learnersData.asyncList.state.userGroupIds;
    onAddLearnerAndGroup(learnerIds, groupIds || [], importedUserGroupIds, removedUserIds);
  }, [
    fetchSelectedGroupIds,
    groupData,
    groupsFilterManager,
    isImportInProgress,
    learnersData.asyncList.state.excludeIds,
    learnersData.asyncList.state.includeIds,
    learnersData.asyncList.state.userGroupIds,
    onAddLearnerAndGroup,
    selectedUserGroupIds,
    showSnackbar,
    t,
  ]);

  return {
    data: dialogData[inViewTab],
    onSubmit,
    tabs,
    inViewTab,
    isSubmitting,
    setImportJobStatus,
    isImportInProgress,
  };
}
